import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Typography } from '@mui/material';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { COLOR_PALETTE } from 'constants/ui';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useInfiniteScroll from 'hooks/useInfiniteScroll';

import { getUuid } from 'store/auth/selectors';
import {
  fetchNotificationsAction,
  markReadAction,
  toggleImportantAction,
} from 'store/pushNotifications/actions';
import {
  selectPushNotificationFilters,
  selectPushNotificationGroups,
  selectPushNotificationHasMore,
  selectPushNotificationItems,
  selectPushNotificationNextCursor,
} from 'store/pushNotifications/selectors';

import PushNotificationGroup from '../PushNotificationGroup';
import PushNotificationSkeleton from '../PushNotificationSkeleton';

import { emptyStateStyles, feedContainerStyles } from './PushNotificationFeed.styles';

const PushNotificationFeed = ({ isLoading, onClose, scrollContainerRef, setIsLoading }) => {
  const dispatch = useDispatch();

  const userId = useSelector(getUuid);
  const items = useSelector(selectPushNotificationItems);
  const groups = useSelector(selectPushNotificationGroups);
  const nextCursor = useSelector(selectPushNotificationNextCursor);
  const hasMore = useSelector(selectPushNotificationHasMore);
  const filters = useSelector(selectPushNotificationFilters);

  const buildFetchParams = useCallback(() => {
    const params = { userId };

    if (!_.isNil(filters.type)) {
      params.type = filters.type;
    }

    if (!_.isNil(filters.source)) {
      params.source = filters.source;
    }

    return params;
  }, [userId, filters]);

  useEffectSafeStrictMode(() => {
    if (!userId) {
      return;
    }

    setIsLoading(true);

    dispatch(fetchNotificationsAction(buildFetchParams()))
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, userId, buildFetchParams, setIsLoading]);

  const loadMore = useCallback(() => {
    if (!userId || !nextCursor) {
      return;
    }

    setIsLoading(true);

    dispatch(fetchNotificationsAction({ ...buildFetchParams(), cursor: nextCursor }))
      .catch(Error)
      .finally(() => setIsLoading(false));
  }, [dispatch, userId, buildFetchParams, nextCursor, setIsLoading]);

  useInfiniteScroll({
    ref: scrollContainerRef,
    loadMore,
    isLoading,
    isLoadMorePagesAvailable: hasMore,
  });

  const onCardClick = useCallback(
    notification => {
      if (!_.isNil(notification.readDate) || !userId) {
        return;
      }

      dispatch(markReadAction({ userId, notificationId: notification.id }));
    },
    [dispatch, userId],
  );

  const onToggleImportant = useCallback(
    notification => {
      if (!userId) {
        return;
      }

      dispatch(
        toggleImportantAction({
          userId,
          notificationId: notification.id,
          isImportant: !notification.isImportant,
        }),
      );
    },
    [dispatch, userId],
  );

  if (!isLoading && !items.length) {
    const hasActiveFilters = !_.isNil(filters.type) || !_.isNil(filters.source);

    return (
      <Box sx={emptyStateStyles}>
        <Typography color={COLOR_PALETTE.GRAY[6]} variant="medium14">
          {hasActiveFilters ? 'No matching notifications' : 'No notifications yet'}
        </Typography>
      </Box>
    );
  }

  if (isLoading && !items.length) {
    return (
      <Box sx={feedContainerStyles}>
        <PushNotificationSkeleton />
      </Box>
    );
  }

  return (
    <Box sx={feedContainerStyles}>
      {groups.map(group => (
        <PushNotificationGroup
          group={group}
          key={group.groupId}
          onCardClick={onCardClick}
          onClose={onClose}
          onToggleImportant={onToggleImportant}
        />
      ))}
    </Box>
  );
};

PushNotificationFeed.propTypes = {
  ...WithLoadingPropTypes,
  onClose: PropTypes.func.isRequired,
  scrollContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export default withLoading(PushNotificationFeed);
